import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { hasRole, isManager, useImpersonation, useLogout, useMe } from '../lib/session';
import { ROLE_LABELS } from '../lib/format';
import type { CurrentUser } from '../lib/types';
import { ErrorMessage } from './ui';
import { Pii } from './Pii';
import { setPrivacyBlur, usePrivacyBlur } from '../lib/privacy';

interface NavItem {
  to: string;
  label: string;
}

export function Layout() {
  const { data: me } = useMe();
  const privacy = usePrivacyBlur();
  // Administrators (also while viewing as someone) can hide personal data on screen.
  const canHide = !!me && (hasRole(me, 'Admin') || !!me.impersonator);
  useEffect(() => {
    document.documentElement.classList.toggle('privacy-blur', privacy);
  }, [privacy]);
  const logout = useLogout();
  // Phone: the tabs and the user menu fold into a hamburger menu (CSS shows the toggle below 800px).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuToggle = useRef<HTMLButtonElement>(null);
  const closeMenu = () => setMenuOpen(false);
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      menuToggle.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);
  const unread = useQuery({
    queryKey: ['conversations', 'unread'],
    queryFn: () => api.get<{ count: number }>('/api/v1/conversations/unread'),
    enabled: !!me && !me.consentRequired && !me.mfaSetupRequired && hasRole(me, 'Patient', 'Therapist'),
    refetchInterval: 60_000,
  });
  const inquiries = useQuery({
    queryKey: ['inquiries', 'unread'],
    queryFn: () => api.get<{ count: number }>('/api/v1/inquiries/unread'),
    enabled: !!me && !me.consentRequired && !me.mfaSetupRequired && hasRole(me, 'Therapist', 'Reception', 'Admin'),
    // A visitor waits about 2 minutes in the website chat for a live answer – new questions must show up quickly,
    // also while the panel is in a background tab.
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
  });

  const inquiriesItem: NavItem = {
    to: '/pytania',
    label: (inquiries.data?.count ?? 0) > 0 ? `Pytania ze strony (${inquiries.data?.count})` : 'Pytania ze strony',
  };

  const hasBadge = (unread.data?.count ?? 0) > 0 || (inquiries.data?.count ?? 0) > 0;

  const items: NavItem[] = [];
  if (me && !me.consentRequired && !me.mfaSetupRequired) {
    if (hasRole(me, 'Patient')) {
      items.push({ to: '/wizyty', label: 'Moje wizyty' }, { to: '/rezerwacja', label: 'Umów wizytę' });
    }
    if (hasRole(me, 'Therapist')) {
      items.push(
        { to: '/kalendarz', label: 'Kalendarz' },
        { to: '/dostepnosc', label: 'Godziny przyjęć' },
        { to: '/pacjenci', label: 'Pacjenci' },
        { to: '/raport', label: 'Raport wizyt' },
        inquiriesItem,
        { to: '/profil', label: 'Mój profil' },
      );
    }
    if (hasRole(me, 'Patient', 'Therapist')) {
      const count = unread.data?.count ?? 0;
      items.push({ to: '/wiadomosci', label: count > 0 ? `Wiadomości (${count})` : 'Wiadomości' });
    }
    if (isManager(me)) {
      items.push(
        { to: '/placowka/kalendarz', label: 'Kalendarz placówki' },
        { to: '/placowka/pacjenci', label: 'Pacjenci' },
        { to: '/placowka/terapeuci', label: 'Terapeuci' },
        { to: '/placowka/raporty', label: 'Raporty' },
      );
      if (!hasRole(me, 'Therapist')) items.push(inquiriesItem);
    }
    if (hasRole(me, 'Admin')) {
      items.push(
        { to: '/placowka/uslugi', label: 'Usługi' },
        { to: '/placowka/gabinety', label: 'Lokalizacje i gabinety' },
        { to: '/placowka/audyt', label: 'Dziennik zdarzeń' },
        { to: '/placowka/powiadomienia', label: 'Wysyłka SMS i e-mail' },
        { to: '/placowka/import', label: 'Import danych' },
        { to: '/placowka/wiadomosci', label: 'Szablony wiadomości' },
        { to: '/placowka/osadzanie', label: 'Osadzanie na stronie' },
        { to: '/placowka/ustawienia', label: 'Ustawienia' },
      );
    }
  }

  return (
    <>
      {(me?.impersonator || privacy) && (
        <div className="floating-bars">
          {me?.impersonator && <ImpersonationBar me={me} />}
          {privacy && (
            <div className="privacy-bar" role="region" aria-label="Dane osobowe ukryte">
              <p>Dane osobowe są ukryte – imiona, nazwiska, e-maile i telefony są rozmyte.</p>
              <button type="button" onClick={() => setPrivacyBlur(false)}>
                Pokaż dane
              </button>
            </div>
          )}
        </div>
      )}
      <a className="skip-link" href="#main">
        Przejdź do treści
      </a>
      <header className="app-header">
        <div className="brand">HyggeCare</div>
        {me && (
          <button
            type="button"
            ref={menuToggle}
            className="menu-toggle secondary"
            aria-expanded={menuOpen}
            aria-controls="app-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="menu-icon" aria-hidden="true" />
            {menuOpen ? 'Zamknij menu' : 'Menu'}
            {!menuOpen && hasBadge && (
              <span className="menu-badge">
                <span className="visually-hidden"> – nowe wiadomości lub pytania</span>
              </span>
            )}
          </button>
        )}
        {me && (
          <div id="app-nav" className={menuOpen ? 'app-nav open' : 'app-nav'}>
            <nav aria-label="Główna nawigacja">
              <ul>
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink to={item.to} onClick={closeMenu}>
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="user-menu">
              <NavLink to="/konto" onClick={closeMenu}>
                <Pii>{me.displayName ?? me.email}</Pii>
                <span className="role"> ({me.roles.map((r) => ROLE_LABELS[r]).join(', ')})</span>
              </NavLink>
              {canHide && !privacy && (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    closeMenu();
                    setPrivacyBlur(true);
                  }}
                >
                  Ukryj dane osobowe
                </button>
              )}
              <button type="button" className="link-button" onClick={() => void logout()}>
                Wyloguj
              </button>
            </div>
          </div>
        )}
      </header>
      <main id="main" className="app-main">
        <Outlet />
      </main>
      <footer className="app-footer">
        <small>Wersja {__APP_VERSION__}</small>
      </footer>
    </>
  );
}

/** Floating bar while an administrator views the application as another user (read-only). */
function ImpersonationBar({ me }: { me: CurrentUser }) {
  const { stop } = useImpersonation();
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="impersonation-bar" role="region" aria-label="Podgląd jako inny użytkownik">
      <p>
        Podgląd jako{' '}
        <strong>
          <Pii>{me.displayName ?? me.email}</Pii>
        </strong> ({me.roles.map((r) => ROLE_LABELS[r]).join(', ')}) – widzisz to co ta osoba, zmiany są
        zablokowane. Zalogowany administrator: <Pii>{me.impersonator}</Pii>.
      </p>
      <button type="button" onClick={() => void stop().catch(setError)}>
        Wyjdź z podglądu
      </button>
      <ErrorMessage error={error} />
    </div>
  );
}
